"use client";

import React from "react";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Send, Loader2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="31495" id="contato" ref={ref} className={`py-24 bg-secondary-100 text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Form Column */}
          <div className="flex flex-col justify-center">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-default font-bold tracking-tight text-foreground mb-4">
                Solicite um Orçamento
              </h2>
              <p className="text-foreground/80 font-sans text-lg">
                Nossa equipe técnica está pronta para oferecer a melhor solução em equipamentos clínicos para sua unidade.
              </p>
            </div>

            <div className="bg-background rounded-lg border border-secondary-300 p-6 md:p-8 shadow-2xs">
              <FormProvider formKey="contact_form_doctoriceinstitutionalwebsite" formId="contact_form_doctoriceinstitutionalwebsite" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              message: {
                required: true
              }
            }} submitText="Solicitar Proposta" successMessage="Sua solicitação foi enviada com sucesso! Um de nossos consultores entrará em contato em breve." errorMessage="Ocorreu um erro ao enviar sua mensagem. Por favor, tente novamente ou use nossos canais diretos.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>

          {/* Info Column */}
          <div className="flex flex-col justify-center space-y-10">
            <div>
              <h3 className="text-2xl font-default font-semibold text-foreground mb-6">
                Canais de Atendimento
              </h3>
              <p className="text-foreground/80 font-sans mb-8">
                Prefere um atendimento imediato? Utilize nossos canais diretos de comunicação ou visite nossas redes sociais.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg text-primary shrink-0">
                    <Mail className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-default font-medium text-foreground">E-mail</h4>
                    <Link to="mailto:atendimento@doctorice.com.br" className="text-foreground/70 font-sans hover:text-primary transition-colors">sac@doctorice.com.br</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg text-primary shrink-0">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-default font-medium text-foreground">Telefone e WhatsApp</h4>
                    <Link to="tel:+551130000000" className="text-foreground/70 font-sans hover:text-primary transition-colors">(14) 3324 5757</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg text-primary shrink-0">
                    <MapPin className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-default font-medium text-foreground">Sede Comercial</h4>
                    <p className="text-foreground/70 font-sans">Av. Getulio Vargas, 470 - Ourinhos - SP</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-secondary-300">
              <h4 className="font-default font-medium text-foreground mb-4">Acompanhe a Doctorice</h4>
              <div className="flex gap-4">
                <Link to="https://instagram.com" newTab className="bg-primary/10 hover:bg-primary/20 text-primary p-3 rounded-full transition-colors" aria-label="Instagram">
                  <Instagram className="size-5" />
                </Link>
                <Link to="https://facebook.com" newTab className="bg-primary/10 hover:bg-primary/20 text-primary p-3 rounded-full transition-colors" aria-label="Facebook">
                  <Facebook className="size-5" />
                </Link>
                <Link to="https://linkedin.com" newTab className="bg-primary/10 hover:bg-primary/20 text-primary p-3 rounded-full transition-colors" aria-label="LinkedIn">
                  <Linkedin className="size-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-primary/10 text-primary p-4 rounded-full">
          <Send className="size-8" />
        </div>
        <h3 className="text-xl font-default font-semibold text-foreground">Solicitação Enviada!</h3>
        <p className="text-foreground/70 font-sans max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5">
          Enviar nova solicitação
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Nome é obrigatório"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Nome Completo</FormLabel>
            <FormControl>
              <Input placeholder="Digite seu nome" className="bg-background border-secondary-300 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-mail é obrigatório",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "E-mail inválido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">E-mail Profissional</FormLabel>
            <FormControl>
              <Input type="email" placeholder="seu@email.com" className="bg-background border-secondary-300 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Telefone / WhatsApp</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(00) 00000-0000" className="bg-background border-secondary-300 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Mensagem é obrigatória"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground">Como podemos ajudar?</FormLabel>
            <FormControl>
              <Textarea placeholder="Descreva brevemente sua necessidade técnica ou comercial..." className="min-h-[120px] resize-y bg-background border-secondary-300 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary-600 rounded-lg font-default" size="lg">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Processando...
          </> : "Solicitar Proposta"}
      </Button>
    </Form>;
}